import { LogOut, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HoverPopover } from "./HoverPopover";
import { MenuLink } from "./MenuLink";
import { NotificationItem } from "./NotificationItem";
import { LoginPanel } from "./LoginPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { AddUnitModal } from "@/components/AddUnitModal";
import { CartFlyout } from "@/components/cart/CartFlyout";
import { useCart } from "@/contexts/CartContext";
import { useServiceMenuNavigation } from "@/hooks/useServiceMenuNavigation";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { useCapabilities } from "@/contexts/CapabilitiesContext";
import { useHidePrices } from "@/contexts/HidePricesContext";
import { Skeleton } from "@/components/ui/skeleton";

const mockNotifications = [
  { text: "RGAs Pending Parts Return", count: 6 },
  { text: "Claim Requires Feedback", count: 3 },
  { text: "New Service Campaigns", count: 2 }
];

export function ActionButtons() {
  const [showLoginPanel, setShowLoginPanel] = useState(false);
  const { hidePrices, toggleHidePrices } = useHidePrices();
  const { user, logout } = useAuth();
  const { totalItems, activeCartSecretId, refreshKey } = useCart();
  const serviceMenu = useServiceMenuNavigation();
  const { isFeatureEnabled, flags } = useFeatureFlags();
  const { hasCapability } = useCapabilities();
  const canAddToCart = hasCapability('AccessUser_AllowAddToCart');
  const renderMockMappings = isFeatureEnabled('renderMockMappings');
  
  // Debug effect to track state changes
  useEffect(() => {
    console.log('ActionButtons: State changed - totalItems =', totalItems, 'activeCartSecretId =', activeCartSecretId, 'refreshKey =', refreshKey);
  }, [totalItems, activeCartSecretId, refreshKey]);

  const handleLogout = () => {
    logout();
  };

  // Determine which notification items to show
  

  if (serviceMenu.isLoading) {
    return (
      <div className="flex items-center space-x-1 md:space-x-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-9 rounded-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-1 md:space-x-2">
      {/* Add VIN Button */}
      {serviceMenu.addVin.visible && (
        <AddUnitModal tooltipLabel={serviceMenu.addVin.name} />
      )}
      
      {/* Notifications */}
      {serviceMenu.notifications.visible && serviceMenu.notificationTotal > 0 && (
        <HoverPopover
          contentClassName="w-80 p-0 border-0 outline-none ring-0"
          trigger={
            <Button 
              variant="ghost" 
              size="icon-md" 
              className="smooth-hover relative group hover:scale-105 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <serviceMenu.notifications.icon className="group-hover:animate-bell-shake" />
              <Badge
                variant="destructive"
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs font-medium"
              >
                {serviceMenu.notificationTotal > 99 ? '99+' : serviceMenu.notificationTotal}
              </Badge>
            </Button>
          }
        >
          <div className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Notifications</h3>
            <div className="space-y-2">
              {serviceMenu.useMock && mockNotifications.map((notification, index) => (
                <NotificationItem 
                  key={index}
                  text={notification.text}
                  count={notification.count}
                  isMapped={false}
                />
              ))}
              {!serviceMenu.useMock && serviceMenu.apiNotifications.map((notification, index) => (
                <NotificationItem 
                  key={index}
                  text={notification.name}
                  count={notification.amount}
                  isMapped={!flags.useMockNotifications}
                />
              ))}
            </div>
          </div>
        </HoverPopover>
      )}
      {serviceMenu.notifications.visible && serviceMenu.notificationTotal === 0 && (
        <Button 
          variant="ghost" 
          size="icon-md" 
          className="smooth-hover relative group hover:scale-105 focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <serviceMenu.notifications.icon />
          <Badge
            variant="secondary"
            className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs font-medium bg-muted-foreground/40 text-muted-foreground"
          >
            0
          </Badge>
        </Button>
      )}
      
      {/* Cart Button with Flyout */}
      {serviceMenu.cart.visible && canAddToCart && (
        <CartFlyout>
          <Button 
            variant="ghost" 
            size="icon-md" 
            className="smooth-hover relative group transition-all duration-200 hover:scale-105"
          >
            <serviceMenu.cart.icon className="transition-transform group-hover:scale-110" />
            <Badge
              key={`cart-badge-${activeCartSecretId}-${totalItems}`}
              variant={totalItems > 0 ? "destructive" : "secondary"}
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs font-medium animate-fade-in"
            >
              {totalItems > 99 ? '99+' : totalItems}
            </Badge>
          </Button>
        </CartFlyout>
      )}
      
      {/* User Menu */}
      {serviceMenu.userMenu.visible && (
        <HoverPopover
          contentClassName="w-56 p-0 border-0 outline-none ring-0"
          trigger={
            <Button 
              variant="ghost" 
              size="icon-md" 
              className="smooth-hover hover:scale-105 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <serviceMenu.userMenu.icon />
            </Button>
          }
        >
          <div className="py-2 min-w-40">
            <div className="px-4 py-2 text-sm text-muted-foreground border-b border-border">
              {user?.username}
            </div>
            {serviceMenu.userMenuItems.map((item) => (
              <MenuLink key={item.title} href={item.url}>
                {item.title}
              </MenuLink>
            ))}
            <div className="px-4 py-3 border-t border-border">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </HoverPopover>
      )}
      
      {/* Dealer Menu */}
      {serviceMenu.dealerMenu.visible && (
        <HoverPopover
          contentClassName="w-56 p-0 border-0 outline-none ring-0"
          trigger={
            <Button 
              variant="ghost" 
              size="icon-md" 
              className="smooth-hover hover:scale-105 focus-visible:ring-0 focus-visible:ring-offset-0"
            >
              <serviceMenu.dealerMenu.icon />
            </Button>
          }
        >
          <div className="py-2">
            {serviceMenu.dealerMenuItems.map((item) => (
              <MenuLink key={item.title} href={item.url}>
                {item.title}
              </MenuLink>
            ))}
            <div className="px-4 py-3 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleHidePrices}
                className="w-full justify-start"
              >
                {hidePrices ? (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Show Prices
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 mr-2" />
                    Hide Prices
                  </>
                )}
              </Button>
            </div>
          </div>
        </HoverPopover>
      )}

      {showLoginPanel && (
        <LoginPanel onClose={() => setShowLoginPanel(false)} />
      )}
    </div>
  );
}
